import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Link, Text, VStack } from '@bonusly/design-system';

import { EmployeeSpotlightSection } from '../../../schemas/spotlight';

interface UserSpotlightDebugBubblesProps {
	section: EmployeeSpotlightSection;
	sectionIndex: number;
	isActive: boolean;
}

export const UserSpotlightDebugBubbles: FC<UserSpotlightDebugBubblesProps> = ({ section, sectionIndex: _sectionIndex, isActive }) => {
	const { t } = useTranslation('spotlight');
	// Only show bubbles for active section
	if (!isActive) {
		return null;
	}
	// Get only the three main scores with their corresponding notes
	const scores = section?.scores
		? [
				{
					key: 'culture',
					score: section.scores['cultureScore'],
					notes: section.scores['cultureNotes'],
				},
				{
					key: 'groundedness',
					score: section.scores['groundednessScore'],
					notes: section.scores['groundednessNotes'],
				},
				{
					key: 'trendsetter',
					score: section.scores['trendsetterScore'],
					notes: section.scores['trendsetterNotes'],
				},
			].filter((item) => item.score !== undefined && item.score !== null)
		: [];

	const supportingEvidence = section?.supportingEvidence || [];
	const strategy = section?.strategy;

	// Map strategy to vendor and model (hardcoded on frontend)
	const getStrategyInfo = (strategyValue: string | undefined) => {
		if (strategyValue === 'a') {
			return { vendor: 'Claude', model: 'claude-opus-4-5-20251101' };
		}
		if (strategyValue === 'b') {
			return { vendor: 'OpenAI', model: 'gpt-5.2' };
		}
		return null;
	};

	const strategyInfo = getStrategyInfo(strategy);

	return (
		<>
			{/* Scores on the right */}
			{scores.length > 0 && (
				<Box position="absolute" right={{ base: '-300px', md: '-340px' }} top={0} zIndex={10} pointerEvents="none" display={{ base: 'none', lg: 'block' }}>
					<VStack spacing={3} align="stretch">
						<Text fontSize="md" color="text.default" fontWeight="semibold" mb={1}>
							{t('debug.aiReviews')}
						</Text>
						{scores.map((item, idx) => {
							const scoreValue = typeof item.score === 'number' ? Math.round(item.score) : String(item.score);
							const notes = typeof item.notes === 'string' ? item.notes : '';
							return (
								<Box
									key={idx}
									bg="bg.surface"
									p={4}
									borderRadius="md"
									border="1px solid"
									borderColor="border.default"
									boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
									w="300px"
								>
									<Text fontSize="md" color="text.default" fontWeight="semibold">
										{item.key}: {scoreValue}
									</Text>
									{notes && (
										<Text fontSize="md" color="text.soft" mt={2} lineHeight="tall">
											{notes}
										</Text>
									)}
								</Box>
							);
						})}
					</VStack>
				</Box>
			)}

			{/* Supporting evidence on the left */}
			{(supportingEvidence.length > 0 || strategyInfo) && (
				<Box position="absolute" left={{ base: '-300px', md: '-340px' }} top={0} zIndex={10} pointerEvents="none" display={{ base: 'none', lg: 'block' }}>
					<VStack spacing={3} align="stretch">
						{supportingEvidence.length > 0 && (
							<>
								<Text fontSize="md" color="text.default" fontWeight="semibold" mb={1}>
									{t('debug.supportingEvidence')}
								</Text>
								{supportingEvidence.map((evidence, idx) => {
									// Handle both string (legacy) and object formats
									const displayText = typeof evidence === 'string' ? evidence : evidence.excerpt;
									const sourceLabel = typeof evidence === 'object' ? (evidence.bonusId ? t('debug.sourceBonus') : evidence.source) : null;

									return (
										<Box
											key={idx}
											bg="bg.surface"
											p={4}
											borderRadius="md"
											border="1px solid"
											borderColor="border.default"
											boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
											w="300px"
										>
											{sourceLabel && (
												<Text fontSize="xs" color="text.softer" mb={1} textTransform="uppercase" letterSpacing="wide">
													{typeof evidence === 'object' && evidence.bonusId ? (
														<Link href={`/bonuses/${evidence.bonusId}`} target="_blank" color="text.interactive" pointerEvents="auto">
															{sourceLabel}
														</Link>
													) : (
														sourceLabel
													)}
												</Text>
											)}
											<Text fontSize="md" color="text.default" lineHeight="tall">
												{displayText}
											</Text>
										</Box>
									);
								})}
							</>
						)}
						{strategyInfo && (
							<>
								<Text fontSize="md" color="text.default" fontWeight="semibold" mb={1}>
									Strategy
								</Text>
								<Box bg="bg.surface" p={4} borderRadius="md" border="1px solid" borderColor="border.default" boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)" w="300px">
									<Text fontSize="md" color="text.default">
										{strategyInfo.vendor}
									</Text>
									<Text fontSize="sm" color="text.soft" mt={1}>
										{strategyInfo.model}
									</Text>
								</Box>
							</>
						)}
					</VStack>
				</Box>
			)}
		</>
	);
};
